/**
 * AppLoadContext.js - Global app data provider
 *
 * Provides app-wide data that needs to be available on any page load
 */
import { useAuth0 } from '@auth0/auth0-react';
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import { cacheService } from '../services/CacheService';
import spotlightService from '../services/SpotlightService';
import { userDataService } from '../services/UserDataService';
import { useProfile } from './ProfileContext';
import profileService from '../services/ProfileService';

const APP_LOAD_CACHE_KEY = 'SPOTLIGHT_GET_APP_LOAD';
const TOKEN_CACHE_KEY = 'APP_TOKEN';
const API_URL_CACHE_KEY = 'API_URL';
const IMAGE_CDN_CACHE_KEY = 'IMAGE_CDN_URL';

const AppLoadContext = createContext();
export const AppLoadProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth0();
  const { currentProfile, profiles, hasLoadedProfiles, needsProfileSelection } = useProfile();
  const [categories, setCategories] = useState([]);
  const [appData, setAppData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const fetching = useRef(false);

  // Check if we need to wait for profile selection before showing app content
  const needsProfileSelectionBeforeApp = useMemo(() => {
    // If not authenticated, don't need profile selection
    if (!isAuthenticated) {return false;}

    // If profiles haven't loaded yet, wait
    if (!hasLoadedProfiles) {return true;}

    // If we need profile selection, wait
    if (needsProfileSelection()) {return true;}

    // If we have profiles but no current profile, wait
    if (profiles.length > 0 && !currentProfile) {return true;}

    const result = false;

    return result;
  }, [isAuthenticated, hasLoadedProfiles, needsProfileSelection, profiles.length, currentProfile]);

  const fetchAppLoad = useCallback(async () => {
    try {
      if (fetching.current) {return;}
      fetching.current = true;
      setLoading(true);
      setError(null);

      // Wait for SpotlightService to initialize (ensures client token is set)
      console.log('🔄 AppLoadContext: Waiting for SpotlightService initialization...');
      await spotlightService.initializationPromise;
      console.log('✅ AppLoadContext: SpotlightService initialized');

      // Clear AppLoad cache when profile changes to ensure fresh data
      if (currentProfile?._id) {
        cacheService.invalidate(APP_LOAD_CACHE_KEY);

        // Clear UserDataService data when profile changes
        userDataService.clearCurrentUser();
      }

      // Set the user in SpotlightService before calling getAppLoad
      if (user && isAuthenticated) {
        spotlightService.setCurrentUser(user);
        console.log('🦁 AppLoadContext: User set in SpotlightService');
      }

      // If we have a current profile, set it in SpotlightService before appload
      if (currentProfile?._id) {
        console.log('🦁 AppLoadContext: Setting profile token...');
        
        // Get the profile token using cached method and set it in SpotlightService
        try {
          const profileToken = await spotlightService.getProfileTokenWithCache(currentProfile._id);
          if (profileToken) {
            spotlightService.setCurrentProfileToken(currentProfile._id, profileToken);
            console.log('🦁 AppLoadContext: Profile token set, length:', profileToken.length);
          }
        } catch (error) {
          console.warn(
            '🦁 AppLoadProvider: Failed to get profile token, proceeding without it:',
            error,
          );
        }
      }

      const data = await spotlightService.getAppLoad().catch(e => {
        throw new Error(e);
      });

      // Don't initialize UserDataService with AppLoad user data here
      // Profile-specific data (watchlist, resumptions) will be fetched separately
      // after a profile is selected

      // Cache all the data
      cacheService.set(APP_LOAD_CACHE_KEY, data);
      cacheService.set(TOKEN_CACHE_KEY, data.app_data?.token);
      cacheService.set(API_URL_CACHE_KEY, data.app_data?.api_url);
      cacheService.set(IMAGE_CDN_CACHE_KEY, data.app_data?.image_cdn_url);

      const processedCategories = await processAppLoad(data);

      setCategories(processedCategories);
      setAppData(data); // Store full app data for other components to use

      // Pre-calculate hover modal data for all channels (this doesn't require profile data)
      if (processedCategories.length > 0) {
        await userDataService.refreshResumptionsData();
        await userDataService.preCalculateHoverModalData(processedCategories);
      }
    } catch (err) {
      console.error('Error fetching app load data:', err);
      setError(err.message);
    } finally {
      fetching.current = false;
      setLoading(false);
    }
  }, [currentProfile]); // Add currentProfile as dependency

  useEffect(() => {
    // Only fetch appload data if we don't need profile selection
    if (!needsProfileSelectionBeforeApp) {
      fetchAppLoad();
    } else {
      // Keep loading state true while waiting for profile selection
      setLoading(true);
    }
  }, [user, isAuthenticated, currentProfile?._id, needsProfileSelectionBeforeApp, fetchAppLoad]);

  const value = useMemo(
    () => ({
      categories,
      appData,
      loading,
      error,
      retry: fetchAppLoad,
      // Profile selection check
      needsProfileSelection: needsProfileSelectionBeforeApp,
      // Convenience getters for commonly used data
      token: appData?.app_data?.token,
      apiUrl: appData?.app_data?.api_url,
      imageCdnUrl: appData?.app_data?.image_cdn_url,
      // Convenience method to get hover modal data
      getHoverModalData: channelId => userDataService.getHoverModalData(channelId),
      // Method to refresh hover modal data when resumption data changes
      refreshHoverModalData: async () => {
        if (categories.length > 0) {
          await userDataService.refreshResumptionsData();
          await userDataService.preCalculateHoverModalData(categories);
        }
      },
      // User data including resumptions
      userData: appData?.user_data,
      resumptions: appData?.user_data?.resumptions,
    }),
    [categories, appData, loading, error, needsProfileSelectionBeforeApp],
  );

  return <AppLoadContext.Provider value={value}>{children}</AppLoadContext.Provider>;
};

// Custom hook to use the context
export const useAppLoad = () => {
  const context = useContext(AppLoadContext);
  if (!context) {
    throw new Error('useAppLoad must be used within AppLoadProvider');
  }
  return context;
};

// Helper function to process categories
const processAppLoad = async data => {
  if (!data?.homepage?.length) {return [];}

  const categoriesArray = [];

  // Filter website-enabled categories
  data.homepage.forEach(item => {
    // Process channels to preserve custom_fields
    const processedChannels =
      item.channels?.map(channel => ({
        ...channel,
        // Preserve custom_fields if they exist
        custom_fields: channel.custom_fields || undefined,
      })) || [];

    const processedCategory = {
      _id: item.category._id,
      name: item.category.name,
      display_name: item.category.name,
      channels: processedChannels,
      slug: item.category.slug,
      style: item.category.style, // Include style property for top-number styling
      logo: item.category.logo, // Include logo for category branding
      wallpaper: item.category.wallpaper, // Include wallpaper for category page backgrounds
    };

    // Extract style from custom_fields if it exists
    if (item.category.custom_fields && Array.isArray(item.category.custom_fields)) {
      const styleField = item.category.custom_fields.find(field => field.field_title === 'style');
      if (styleField && styleField.field_value) {
        processedCategory.style = styleField.field_value;
      }
    }

    categoriesArray.push(processedCategory);
  });

  return categoriesArray;
};
